import React, { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
    createPerimeterYardListApi,
    GetPerimeterYard,
    deletePerimeterYardApi,
    updatePerimeterYardApi,
} from "../services/api";

const DEFAULT_CENTER = [-12.0464, -77.0428];
const DEFAULT_ZOOM = 19;
const FOCUSED_ZOOM = 22;

const generateTempId = () => `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function buildVertex(lat, lng, extras = {}) {
    return {
        id: extras.id ?? null,
        tempId: extras.id ?? extras.tempId ?? generateTempId(),
        lat,
        lng,
        order: extras.order ?? null,
    };
}

function normalizeCoordinates(item) {
    let lat = Number(item?.latitude);
    let lng = Number(item?.longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
    }

    if (Math.abs(lat) > 90 && Math.abs(lng) < 90) {
        [lat, lng] = [lng, lat];
    }

    return { lat, lng };
}

const assignOrders = (items = []) =>
    items.map((item, idx) => ({ ...item, order: idx + 1 }));

export default function PerimeterModal({
    show,
    yardId,
    centerLat,
    centerLng,
    token,
    onClose,
    onToast,
}) {
    const mapRef = useRef(null);
    const mapObj = useRef(null);
    const layerRef = useRef(null);

    const [verts, setVerts] = useState([]); // [{ id, tempId, lat, lng }]
    const [saving, setSaving] = useState(false);
    const [updatingMap, setUpdatingMap] = useState({});

    useEffect(() => {
        if (!show) return;

        if (mapRef.current && !mapObj.current) {
            const lat0 =
                typeof centerLat === "number" ? centerLat : parseFloat(centerLat);
            const lng0 =
                typeof centerLng === "number" ? centerLng : parseFloat(centerLng);
            const hasCenter = Number.isFinite(lat0) && Number.isFinite(lng0);

            mapObj.current = L.map(mapRef.current).setView(
                hasCenter ? [lat0, lng0] : DEFAULT_CENTER,
                hasCenter ? FOCUSED_ZOOM : DEFAULT_ZOOM
            );

            L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                attribution: "&copy; OpenStreetMap contributors",
            }).addTo(mapObj.current);

            layerRef.current = L.layerGroup().addTo(mapObj.current);

            mapObj.current.on("click", (e) => {
                const { lat, lng } = e.latlng;
                setVerts((prev) => assignOrders([...prev, buildVertex(lat, lng)]));
            });
        } else if (mapObj.current) {
            setTimeout(() => mapObj.current.invalidateSize(), 0);
            const lat0 =
                typeof centerLat === "number" ? centerLat : parseFloat(centerLat);
            const lng0 =
                typeof centerLng === "number" ? centerLng : parseFloat(centerLng);
            if (Number.isFinite(lat0) && Number.isFinite(lng0)) {
                mapObj.current.setView([lat0, lng0], FOCUSED_ZOOM);
            }
        }
    }, [show, centerLat, centerLng]);

    const handleRemoveVertex = useCallback(
        async (vertex) => {
            if (!vertex) return;

            if (!vertex.id) {
                setVerts((prev) => assignOrders(prev.filter((item) => item.tempId !== vertex.tempId)));
                return;
            }

            try {
                await deletePerimeterYardApi({ id: vertex.id, token });
                setVerts((prev) => assignOrders(prev.filter((item) => item.tempId !== vertex.tempId)));
                onToast?.("success", "Vertice eliminado");
            } catch (err) {
                console.error("Failed to delete vertex", err);
                onToast?.("danger", err?.message || "No se pudo eliminar el vertice");
            }
        },
        [token, onToast]
    );

    const handleUpdateVertex = useCallback(
        async (vertex, index) => {
            if (!vertex) return;
            if (!vertex.id) {
                onToast?.("warning", "Guarda el perimetro para registrar este vertice antes de actualizarlo");
                return;
            }

            const longitude = Number(vertex.lng);
            const latitude = Number(vertex.lat);
            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
                onToast?.("danger", "Coordenadas invalidas");
                return;
            }

            setUpdatingMap((prev) => ({ ...prev, [vertex.tempId]: true }));
            const order = index + 1;
            try {
                await updatePerimeterYardApi({
                    id: vertex.id,
                    order,
                    longitude,
                    latitude,
                    token,
                });
                setVerts((prev) =>
                    assignOrders(
                        prev.map((item) =>
                            item.tempId === vertex.tempId ? { ...item, order } : item
                        )
                    )
                );
                onToast?.("success", "Vertice actualizado");
            } catch (err) {
                console.error("Failed to update vertex", err);
                onToast?.("danger", err?.message || "No se pudo actualizar el vertice");
            } finally {
                setUpdatingMap((prev) => {
                    const next = { ...prev };
                    delete next[vertex.tempId];
                    return next;
                });
            }
        },
        [token, onToast]
    );

    useEffect(() => {
        if (!mapObj.current || !layerRef.current) return;
        const layer = layerRef.current;
        layer.clearLayers();

        const latlngs = verts.map((v) => L.latLng(v.lat, v.lng));

        if (latlngs.length >= 3) {
            L.polygon(latlngs, {
                color: "#0d6efd",
                fillColor: "#0d6efd",
                fillOpacity: 0.15,
            }).addTo(layer);
        } else if (latlngs.length >= 2) {
            L.polyline(latlngs, { color: "#0d6efd", dashArray: "5 5" }).addTo(layer);
        }

        latlngs.forEach((ll, i) => {
            const vertex = verts[i];
            const orderValue = Number(vertex?.order);
            const label = Number.isFinite(orderValue) && orderValue > 0 ? orderValue : i + 1;
            const isPersisted = Boolean(vertex?.id);
            const color = isPersisted ? "#0d6efd" : "#6c757d";
            const marker = L.marker(ll, {
                draggable: true,
                icon: L.divIcon({
                    className: "gp-vertex",
                    html: `<div style="width:28px;height:28px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:600;font-size:12px;">${label}</div>`,
                    iconSize: [28, 28],
                    iconAnchor: [14, 14],
                }),
            }).addTo(layer);

            marker.on("dragend", (e) => {
                const { lat, lng } = e.target.getLatLng();
                setVerts((prev) =>
                    assignOrders(
                        prev.map((item) =>
                            item.tempId === vertex.tempId ? { ...item, lat, lng } : item
                        )
                    )
                );
            });

            marker.on("contextmenu", () => {
                handleRemoveVertex(vertex);
            });
        });

        try {
            const layers = layer.getLayers();
            if (layers.length) {
                const group = L.featureGroup(layers);
                mapObj.current.fitBounds(group.getBounds().pad(0.2));
            }
        } catch (err) {
            console.error("fitBounds failed", err);
        }
    }, [verts, handleRemoveVertex]);

    useEffect(() => {
        if (!show || !yardId) return;

        let cancelled = false;

        const loadPerimeter = async () => {
            try {
                const records = await GetPerimeterYard({ yardId, token });
                if (cancelled) return;

                const sorted = Array.isArray(records) ? [...records] : [];
                sorted.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

                const mapped = sorted
                    .map((item) => {
                        const coords = normalizeCoordinates(item);
                        if (!coords) return null;
                        return buildVertex(coords.lat, coords.lng, {
                            id: item?.id ?? null,
                            order: item?.order ?? null,
                        });
                    })
                    .filter(Boolean);

                setVerts(assignOrders(mapped));
            } catch (err) {
                if (!cancelled) {
                    console.error("Failed to load perimeter", err);
                    onToast?.("danger", "No se pudo cargar el perimetro existente");
                }
            }
        };

        loadPerimeter();

        return () => {
            cancelled = true;
        };
    }, [show, yardId, token, onToast]);

    useEffect(() => {
        if (!show) {
            setVerts([]);
            setSaving(false);
        }
    }, [show]);

    // const undo = () => setVerts((prev) => assignOrders(prev.slice(0, -1)));
    // const clearAll = () => setVerts([]);

    const save = async () => {
        try {
            if (!yardId) {
                onToast?.("danger", "Patio invalido");
                return;
            }
            if (verts.length < 3) {
                onToast?.("danger", "Agrega al menos 3 vertices");
                return;
            }
            setSaving(true);

            const items = verts.map((vertex, index) => ({
                yardId,
                order: index + 1,
                longitude: Number(vertex.lng),
                latitude: Number(vertex.lat),
            }));

            await createPerimeterYardListApi({ items, token });
            onToast?.("success", "Perimetro guardado");
            onClose?.();
        } catch (e) {
            onToast?.("danger", e?.message || "Error al guardar perimetro");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div id='perimeterModal' className={`modal fade ${show ? "show d-block" : ""}`} tabIndex="-1" role="dialog" aria-modal={show}>
            <div className="modal-dialog modal-fullscreen modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header mx-5">
                        <h5 className="modal-title">Perimetro del patio</h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <div className="modal-body">
                        <div className="mb-2 d-flex align-items-center gap-2 flex-wrap">
                            <span className="text-muted small">
                                Clic en el mapa para agregar vertices - Arrastra para mover - Clic derecho para borrar
                            </span>
                            {/* <div className="ms-auto d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={undo}
                                    disabled={!verts.length}
                                >
                                    Deshacer
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={clearAll}
                                    disabled={!verts.length}
                                >
                                    Limpiar
                                </button>
                            </div> */}
                        </div>

                        <div className="row g-3">
                            <div className="col-12 col-lg-8">
                                <div ref={mapRef} style={{ height: '90vh', borderRadius: 8, overflow: "hidden" }} />
                            </div>
                            <div className="col-12 col-lg-4">
                                <div className="card h-100">
                                    <div className="card-header py-2">Vertices ({verts.length})</div>
                                    <div className="card-body p-2" style={{ maxHeight: 380, overflow: "auto" }}>
                                        {verts.length === 0 && (
                                            <div className="text-muted small">Sin vertices. Haz clic en el mapa para agregar.</div>
                                        )}
                                        {verts.map((vertex, i) => (
                                            <div key={vertex.tempId} className="border rounded p-2 mb-2 d-flex align-items-center gap-2">
                                                <span className="badge bg-secondary">{i + 1}</span>
                                                <input
                                                    type="number"
                                                    step="0.000001"
                                                    className="form-control form-control-sm"
                                                    style={{ width: 140 }}
                                                    value={vertex.lat}
                                                    onChange={(e) => {
                                                        const value = Number(e.target.value);
                                                        setVerts((prev) =>
                                                            assignOrders(
                                                                prev.map((item, idx) =>
                                                                    idx === i ? { ...item, lat: Number.isFinite(value) ? value : 0 } : item
                                                                )
                                                            )
                                                        );
                                                    }}
                                                />
                                                <input
                                                    type="number"
                                                    step="0.000001"
                                                    className="form-control form-control-sm"
                                                    style={{ width: 140 }}
                                                    value={vertex.lng}
                                                    onChange={(e) => {
                                                        const value = Number(e.target.value);
                                                        setVerts((prev) =>
                                                            assignOrders(
                                                                prev.map((item, idx) =>
                                                                    idx === i ? { ...item, lng: Number.isFinite(value) ? value : 0 } : item
                                                                )
                                                            )
                                                        );
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    title="Eliminar"
                                                    onClick={() => handleRemoveVertex(vertex)}
                                                >

                                                    <i className="bi bi-trash"></i>
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success"
                                                    title="Actualizar"
                                                    onClick={() => handleUpdateVertex(vertex, i)}
                                                    disabled={!vertex.id || updatingMap[vertex.tempId]}
                                                >
                                                    {updatingMap[vertex.tempId] ? (
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    ) : (
                                                        <i className="bi bi-pencil"></i>
                                                    )}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="card-footer py-2 text-end">
                                        <button className="btn btn-sm btn-primary" onClick={save} disabled={saving || verts.length < 3}>
                                            {saving ? "Guardando..." : "Guardar"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


